import type { Application } from "pixi.js";

/**
 * ¿Es el `OperationError` de WebGPU al subir un `<video>` sin frame decodificado
 * a una textura GPU? Se matchea por las FRASES ESPECÍFICAS del error de Chromium
 * (no por el nombre de la API `copyExternalImageToTexture` a secas, que aparece
 * en cualquier fallo de ese upload) para no tragar otros errores. El mensaje:
 *   "Failed to execute 'copyExternalImageToTexture' on 'GPUQueue': Failed to
 *    import texture from video element that doesn't have back resource."
 */
export function isTransientVideoUploadError(err: unknown): boolean {
  // El OperationError de WebGPU se lanza como `DOMException`, que NO siempre es
  // `instanceof Error` (lo es en el Chromium actual de Electron, pero depende de
  // la versión del engine). Hacemos duck-typing sobre `.message` para cubrir
  // Error, DOMException y cualquier objeto-error con mensaje string.
  const message =
    typeof err === "object" && err !== null && "message" in err
      ? String((err as { message: unknown }).message)
      : "";

  return (
    message.includes("doesn't have back resource") ||
    message.includes("import texture from video")
  );
}

/**
 * Cinturón de seguridad para el upload de video a textura GPU en Pixi (WebGPU).
 *
 * El browser source (y cámara/video/alertas) se renderiza como un `<video>` que
 * Pixi sube a una textura GPU. Cuando el `<video>` no tiene un frame decodificado
 * válido en ese instante — el MediaStream del browser source hipa, el video
 * recién carga (readyState HAVE_METADATA, 0 frames), o cambia de resolución
 * mid-stream — WebGPU lanza el `OperationError` de arriba.
 *
 * Ese upload pasa SIEMPRE por `GpuTextureSystem.onSourceUpdate` (chokepoint),
 * que se invoca desde TRES rutas:
 *   1. el primer bind del source dentro de `renderer.render` (issue DESKTOP-A),
 *   2. `requestVideoFrameCallback` por frame (re-upload continuo), y
 *   3. los handlers DOM del `<video>` — `loadedmetadata`/`resize` → `onSourceResize`
 *      → `initSource` → `onSourceUpdate` (issue DESKTOP-9, la carga inicial y el
 *      resize del browser source).
 * Las rutas 2 y 3 ocurren FUERA de `renderer.render`, así que envolver el render
 * NO alcanza: hay que envolver el chokepoint. Además, si el throw sale de la ruta
 * 3, aborta la continuación de `VideoSource._mediaReady` (no resuelve la carga,
 * no configura el auto-update) → el browser source queda sin reproducir: el
 * síntoma exacto del ticket. Saltear el upload deja que esa continuación siga.
 *
 * Debe instalarse ANTES de crear cualquier textura: `onSourceUpdate` se registra
 * como listener `update`/`resize` de cada source en su init
 * (`source.on("update", this.onSourceUpdate, this)`), capturando la referencia en
 * ese momento. Shadoweando la propiedad en la instancia antes de que exista algún
 * source, todos los registros futuros usan la versión envuelta (y el `off`
 * posterior remueve la misma). Se llama en el init de `PixiFactory`, antes del
 * primer render → cubre editor y OSR de emisión (mismo path). En WebGL2 (fallback)
 * el upload va por `texImage2D`, que no lanza excepción JS (reporta por `getError`),
 * así que el wrap es inocuo ahí.
 */
export function installVideoUploadRenderGuard(app: Application): void {
  const textureSystem = app.renderer.texture as unknown as {
    onSourceUpdate?: (source: unknown) => unknown;
  };

  if (typeof textureSystem?.onSourceUpdate !== "function") return;

  // `onSourceUpdate` es un método del prototipo; ligarlo y reasignar la propiedad
  // en la instancia crea un shadow propio que Pixi usa en todos los `this.
  // onSourceUpdate(...)` y en los `source.on(..., this.onSourceUpdate)` futuros.
  const original = textureSystem.onSourceUpdate.bind(textureSystem) as (
    source: unknown,
  ) => unknown;

  let lastWarnMs = 0;

  textureSystem.onSourceUpdate = (source: unknown): unknown => {
    try {
      return original(source);
    } catch (err) {
      if (isTransientVideoUploadError(err)) {
        // Saltear ESTE upload; el próximo frame/evento con imagen válida sube
        // normal. Warn throttleado (~cada 10s) para dejar rastro si un source
        // quedara trabado, sin spamear la consola a 60fps.
        const now = Date.now();

        if (now - lastWarnMs > 10_000) {
          lastWarnMs = now;

          console.warn(
            "[render] upload de video salteado: el <video> aún no tenía imagen lista para la GPU (se reanuda solo).",
            err,
          );
        }

        return undefined;
      }

      throw err;
    }
  };
}
